import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import styled from 'styled-components'
import { PiArrowSquareOut } from 'react-icons/pi'

// Styled tooltip that matches design
const StyledTooltip = styled(Tooltip)`
  &.show {
    opacity: 1 !important;
  }

  & .tooltip-inner {
    background-color: white !important;
    border: 1px solid #e5e7eb !important;
    border-radius: 6px !important;
    box-shadow:
      0 10px 15px -3px rgba(0, 0, 0, 0.1),
      0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
    padding: 6px 8px !important;
    font-size: 12px !important;
    max-width: none !important;
    text-align: left !important;
    color: #111827 !important;
  }

  &.bs-tooltip-top .tooltip-arrow::before {
    border-top-color: #e5e7eb !important;
  }

  &.bs-tooltip-top .tooltip-arrow::after {
    border-top-color: white !important;
    content: '';
    position: absolute;
    top: -1px;
    left: 0;
    border: 5px solid transparent;
    border-top-color: white;
  }
`

interface ChartItem {
  name: string
  value: number
  color: string
  percentage: number
}

interface LegendConfig {
  title?: string
  showLink?: boolean
  linkHref?: string
  columns?: 1 | 2
  showPercentages?: boolean
}

interface ProgressChartProps {
  // Title variants
  label?: string // Forecast style: "ANNUAL REVENUE OPP."
  value?: string // Forecast style: "$24.3K"
  title?: string // Demographic style: "Age"

  // Override the default label classNames (uppercase tracking-wider). Pass
  // your own className string for a non-uppercase or larger title style.
  labelClassName?: string

  // Chart data
  items: ChartItem[]

  // Chart options
  showScale?: boolean // Show 0%-100% labels
  tooltipValueFormatter?: (item: ChartItem) => string // Custom tooltip value formatter

  // Legend configuration
  legend?: LegendConfig

  // Switches the chart to the homepage's "bare" treatment:
  //   - no white card / shadow around the legend items
  //   - LegendItem uses tighter dot↔label gap + lighter, larger secondary-gray
  //     label text (instead of the legacy uppercase-feeling, font-light spec)
  //   - stacked bar segments get a 2px white border between them so adjacent
  //     gradient shades read as distinct slices
  // Default (false) preserves the legacy company-page sidebar styling.
  bareLegend?: boolean

  // Styling
  className?: string
}

function TooltipContent({ title, value }: { title: string; value: string }) {
  return (
    <div>
      <div style={{ fontWeight: 500, color: '#111827' }}>{title}</div>
      <div style={{ fontWeight: 300, color: '#111827' }}>{value}</div>
    </div>
  )
}

function LegendItem({
  item,
  showPercentage = false,
  bare = false,
}: {
  item: ChartItem
  showPercentage?: boolean
  /** When true, use the homepage's tighter dot↔label gap and lighter,
   *  larger secondary-gray label text. Default preserves the legacy
   *  company-page styling (gap-2, text-xs font-light text-fg). */
  bare?: boolean
}) {
  return (
    <div className={`flex items-center min-w-0 ${bare ? 'gap-1' : 'gap-2'}`}>
      <div
        className="w-2 h-2 rounded-full flex-shrink-0"
        style={{ backgroundColor: item.color }}
      />
      <div
        className={`truncate min-w-0 flex-1 tabular-nums ${
          bare
            ? 'text-sm font-normal text-fg-secondary'
            : 'text-xs font-light text-fg'
        }`}
        title={
          showPercentage ? `${item.name} - ${item.percentage}%` : item.name
        }
      >
        {item.name}
        {showPercentage && <span> - {item.percentage}%</span>}
      </div>
    </div>
  )
}

export function ProgressChart({
  label,
  value,
  title,
  labelClassName,
  items,
  showScale = false,
  tooltipValueFormatter,
  legend,
  bareLegend = false,
  className = '',
}: ProgressChartProps) {
  const hasLegend = legend && items.length > 0

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {/* Title Section */}
      {(label || title) && (
        <div className="flex flex-col gap-2">
          {label && (
            <div
              className={
                labelClassName ??
                'text-xs font-light text-fg uppercase tracking-wider'
              }
            >
              {label}
            </div>
          )}
          {value && (
            <div className="text-base font-semibold text-fg tracking-tight tabular-nums">
              {value}
            </div>
          )}
          {title && (
            <div className="text-base font-semibold text-fg tracking-tight">
              {title}
            </div>
          )}
        </div>
      )}

      {/* Progress Bar — 8px of breathing room above and below the bar. */}
      <div className={showScale ? 'py-[8px] mb-3' : 'py-[8px]'}>
        <div className="w-full h-4 bg-gray-100 rounded-sm overflow-hidden flex">
          {items.map((item, index) => (
            <OverlayTrigger
              key={item.name}
              placement="top"
              overlay={
                <StyledTooltip id={`tooltip-${item.name}`}>
                  <TooltipContent
                    title={item.name}
                    value={
                      tooltipValueFormatter
                        ? tooltipValueFormatter(item)
                        : legend?.showPercentages
                          ? `${item.percentage}%`
                          : item.value.toString()
                    }
                  />
                </StyledTooltip>
              }
            >
              <div
                className={`h-full cursor-pointer ${index === 0 ? 'rounded-l-sm' : ''} ${
                  index === items.length - 1
                    ? 'rounded-r-sm'
                    : bareLegend
                      ? 'border-r-2 border-white'
                      : ''
                }`}
                style={{
                  backgroundColor: item.color,
                  // flex-grow (not an absolute width %) so the segments always
                  // fill the bar — independently rounded percentages sum to ~99%
                  // and an absolute width would leave the gray track showing at
                  // the end. Ratios still match `percentage`, so proportions are
                  // unchanged.
                  flexGrow: item.percentage,
                  flexBasis: 0,
                  minWidth: 0,
                }}
              />
            </OverlayTrigger>
          ))}
        </div>

        {/* Scale Labels */}
        {showScale && (
          <div className="flex justify-between mt-1">
            <span className="text-xs font-light text-fg-secondary uppercase tracking-wider">
              0%
            </span>
            <span className="text-xs font-light text-fg-secondary uppercase tracking-wider">
              100%
            </span>
          </div>
        )}
      </div>

      {/* Legend */}
      {hasLegend && (
        <div
          className={
            bareLegend ? 'mt-2' : 'bg-white rounded-lg p-3 shadow-sm mt-2'
          }
        >
          {/* Legend Header */}
          {(legend.title || legend.showLink) && (
            <div className="flex items-center justify-between mb-3">
              {legend.title && (
                <span className="text-sm font-medium text-fg">
                  {legend.title}
                </span>
              )}
              {legend.showLink && legend.linkHref && (
                <a
                  href={legend.linkHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-brand-active hover:text-basil-800 transition-colors"
                >
                  <PiArrowSquareOut size={14} />
                </a>
              )}
            </div>
          )}

          {/* Legend Items */}
          <div
            className={
              legend.columns === 2 ? 'grid grid-cols-2 gap-2' : 'space-y-2'
            }
          >
            {items.map((item) => (
              <LegendItem
                key={item.name}
                item={item}
                showPercentage={legend.showPercentages}
                bare={bareLegend}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
